import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@nexus/ui";

import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { orderStatusTimeDashboardQueryOptions } from "@/lib/queries";

import { OrderStatusTimeView } from "../components/OrderStatusTimeView";

interface OrderStatusTimePageProps {
  /** Renders under script delivery: h2, and the route owns the document title. */
  embedded?: boolean;
}

export function OrderStatusTimePage({ embedded = false }: OrderStatusTimePageProps) {
  const { t } = useTranslation();
  const title = t("orderStatusTime.title", "Order status time");
  if (embedded) {
    return <OrderStatusTimeBody title={title} headingLevel="h2" />;
  }
  return <OrderStatusTimeStandalone title={title} />;
}

function OrderStatusTimeStandalone({ title }: { title: string }) {
  useDocumentTitle(title);
  return <OrderStatusTimeBody title={title} headingLevel="h1" />;
}

function OrderStatusTimeBody({
  title,
  headingLevel,
}: {
  title: string;
  headingLevel: "h1" | "h2";
}) {
  const { t } = useTranslation();
  const query = useQuery(orderStatusTimeDashboardQueryOptions());
  const orders = query.data?.orders ?? [];

  const Heading = headingLevel;
  const embedded = headingLevel === "h2";

  return (
    <div className={embedded ? "flex flex-col gap-4" : "flex h-full flex-col gap-4 pt-4"}>
      <header className="flex flex-col gap-1">
        <Heading className="text-xl font-semibold">
          {title}
        </Heading>
        <p className="text-sm text-muted-foreground">
          {t(
            "orderStatusTime.subtitle",
            "Average time an order spends in each step. Open a partner to see each client, then a client to see the orders.",
          )}
        </p>
      </header>
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t("orderStatusTime.loading", "Loading…")}
        </p>
      ) : null}
      {query.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm text-destructive">
            {t("orderStatusTime.error", "Could not load order status time")}
          </p>
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t("orderStatusTime.retry", "Try again")}
          </Button>
        </div>
      ) : null}
      {query.isSuccess ? <OrderStatusTimeView orders={orders} /> : null}
    </div>
  );
}
